import { Button, Logo, radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { TenantLogo } from '@/components/TenantLogo.js';
import { useSlackChannelManager, useSlackTest } from '@/lib/notifications.hooks.js';
import { GLYPH_SX, StatusLabel, statusChipSx } from './channelCardShared.js';
import type { SlackChannel, SlackData } from './notifications.types.js';
import { SlackIcon } from './SlackIcon.js';

export type SlackSetupStep = 'redirecting' | 'configure' | 'test';

interface SlackSetupDialogProps {
  open: boolean;
  step: SlackSetupStep;
  slack: SlackData;
  onCancel: () => void;
  onReconnect: () => void;
  onConfigured: (channel: SlackChannel) => void;
  onFinish: () => void;
}

function SlackGlyph({ size = 23 }: { size?: number }): ReactNode {
  return (
    <Box sx={{ ...GLYPH_SX, bgcolor: (theme) => theme.palette.network.slack }}>
      <SlackIcon sx={{ fontSize: size }} />
    </Box>
  );
}

function DialogHeader({
  slack,
  title,
  subtitle,
  showConnected = false,
  onClose,
}: {
  slack: SlackData;
  title: string;
  subtitle: ReactNode;
  showConnected?: boolean;
  onClose: () => void;
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 2,
        px: 4,
        py: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <SlackGlyph />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id="slack-setup-title" component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>
      </Box>
      {showConnected && slack.status === 'connected' && (
        <Chip
          size="small"
          label={<StatusLabel status="connected" />}
          sx={statusChipSx('connected')}
        />
      )}
      <IconButton
        aria-label="Close Slack setup"
        onClick={onClose}
        sx={{
          width: 34,
          height: 34,
          borderRadius: `${radius.lg}px`,
          bgcolor: 'background.platform',
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Stack>
  );
}

function Redirecting({ onCancel }: { onCancel: () => void }): ReactNode {
  const { providerName } = useTenant();
  return (
    <Stack sx={{ alignItems: 'center', px: 6, py: 7, textAlign: 'center' }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 2, mb: 3 }}>
        <SlackGlyph size={25} />
        <LinkRoundedIcon sx={{ color: 'text.secondary', fontSize: 19 }} />
        <Box
          sx={{
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${radius.lg}px`,
            bgcolor: 'background.platform',
          }}
        >
          <TenantLogo variant="glyph" height={27} />
        </Box>
      </Stack>
      <Typography id="slack-setup-title" component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
        Redirecting to Slack…
      </Typography>
      <Typography sx={{ mt: 2, maxWidth: 310, fontSize: 12, color: 'text.secondary' }}>
        Taking you to Slack to authorize {providerName}. You&apos;ll come right back.
      </Typography>
      <CircularProgress size={26} sx={{ mt: 3 }} />
      <Button
        size="small"
        variant="text"
        onClick={onCancel}
        sx={{ mt: 2, color: 'text.secondary' }}
      >
        Cancel
      </Button>
    </Stack>
  );
}

function Configure({
  slack,
  onCancel,
  onReconnect,
  onConfigured,
}: {
  slack: SlackData;
  onCancel: () => void;
  onReconnect: () => void;
  onConfigured: (channel: SlackChannel) => void;
}): ReactNode {
  const { providerName } = useTenant();
  const channelManager = useSlackChannelManager(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const channels = channelManager.channels;
  const missingScope = channelManager.error?.message.includes('missing_scope') === true;

  useEffect(() => {
    if (channels.length === 0 || selectedId !== null) return;
    const current = channels.find((channel) => channel.name === slack.channel);
    setSelectedId((current ?? channels[0])?.id ?? null);
  }, [channels, selectedId, slack.channel]);

  const filteredChannels = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return channels;
    return channels.filter((channel) => channel.name.toLowerCase().includes(needle));
  }, [channels, search]);

  const selected = channels.find((channel) => channel.id === selectedId) ?? null;

  async function continueSetup(): Promise<void> {
    if (selected === null) return;
    setSubmitError(null);
    try {
      await channelManager.saveChannel(selected.id);
      onConfigured(selected);
    } catch {
      setSubmitError('Could not save the default channel. Please try again.');
    }
  }

  return (
    <>
      <DialogHeader
        slack={slack}
        title="Configure Slack delivery"
        subtitle={
          <>
            Connected to <strong>{slack.workspace}</strong>
            {slack.installedBy ? ` · installed by ${slack.installedBy}` : ''}
          </>
        }
        showConnected
        onClose={onCancel}
      />
      <Stack sx={{ px: 4, py: 3, minHeight: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Default channel
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 11, color: 'text.secondary' }}>
          {providerName} posts notifications here unless you route an event elsewhere.
        </Typography>
        <OutlinedInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search channels…"
          type="search"
          slotProps={{ input: { 'aria-label': 'Search channels' } }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </InputAdornment>
          }
          sx={{
            mt: 2,
            height: 39,
            borderRadius: `${radius.lg}px`,
            fontSize: 12,
            bgcolor: 'background.page',
          }}
        />

        <Stack
          role="radiogroup"
          aria-label="Default Slack channel"
          sx={{ mt: 1, maxHeight: 280, overflowY: 'auto', pr: 1 }}
        >
          {channelManager.isPending ? (
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'center', gap: 1, py: 5 }}
            >
              <CircularProgress size={18} />
              <Typography sx={{ color: 'text.secondary' }}>Loading channels…</Typography>
            </Stack>
          ) : channelManager.isError ? (
            <Stack sx={{ alignItems: 'center', py: 4, gap: 1 }}>
              <Alert severity="error">
                {missingScope
                  ? 'Reconnect Slack to allow private-channel discovery.'
                  : 'Could not load Slack channels.'}
              </Alert>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={missingScope ? onReconnect : () => void channelManager.refetch()}
              >
                {missingScope ? 'Reconnect Slack' : 'Try again'}
              </Button>
            </Stack>
          ) : filteredChannels.length === 0 ? (
            <Typography sx={{ py: 4, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
              No matching channels
            </Typography>
          ) : (
            filteredChannels.map((channel) => {
              const checked = selectedId === channel.id;
              return (
                <Box
                  key={channel.id}
                  component="label"
                  sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 49,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    border: '1px solid',
                    borderColor: checked ? 'primary.main' : 'transparent',
                    borderRadius: `${radius.lg}px`,
                    bgcolor: checked ? 'primary.light' : 'transparent',
                    color: 'text.primary',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { bgcolor: checked ? 'primary.light' : 'background.platform' },
                    '&:focus-within': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 1,
                    },
                  }}
                >
                  <Box
                    component="input"
                    type="radio"
                    name="slack-default-channel"
                    value={channel.id}
                    checked={checked}
                    aria-label={`#${channel.name}`}
                    onChange={() => setSelectedId(channel.id)}
                    sx={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
                  />
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: `${radius.md}px`,
                      bgcolor: 'background.platform',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >
                    #
                  </Box>
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{channel.name}</Typography>
                    {channel.description !== '' && (
                      <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        {channel.description}
                      </Typography>
                    )}
                  </Stack>
                  {channel.memberCount !== null && (
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                      {channel.memberCount}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      border: '1px solid',
                      borderColor: checked ? 'primary.main' : 'border.strong',
                      bgcolor: checked ? 'primary.main' : 'background.paper',
                      color: 'common.white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {checked && <CheckIcon sx={{ fontSize: 13 }} />}
                  </Box>
                </Box>
              );
            })
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            mt: 2,
            alignItems: 'flex-start',
            gap: 1,
            p: 2,
            bgcolor: 'primary.light',
            borderRadius: `${radius.lg}px`,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography sx={{ flex: 1, fontSize: 11, color: 'text.secondary' }}>
            <strong>Don&apos;t see a private channel?</strong> In Slack, run{' '}
            <Box component="code" sx={{ color: 'primary.main' }}>
              /invite @Alison
            </Box>{' '}
            in that channel, then recheck.
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<RefreshIcon />}
            disabled={channelManager.isFetching}
            onClick={() => void channelManager.refetch()}
            sx={{ flexShrink: 0, p: 0 }}
          >
            Recheck
          </Button>
        </Stack>
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 2,
          px: 4,
          py: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.page',
        }}
      >
        <Button size="small" variant="text" onClick={onCancel} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {selected && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            Posting to <strong>#{selected.name}</strong>
          </Typography>
        )}
        <Button
          size="small"
          variant="gradient"
          endIcon={
            channelManager.isSaving ? (
              <CircularProgress size={13} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
          disabled={selected === null || channelManager.isSaving}
          onClick={() => void continueSetup()}
        >
          Continue
        </Button>
      </Stack>
    </>
  );
}

function TestMessage({
  slack,
  onCancel,
  onFinish,
}: {
  slack: SlackData;
  onCancel: () => void;
  onFinish: () => void;
}): ReactNode {
  const { providerName } = useTenant();
  const slackTest = useSlackTest();
  const [delivered, setDelivered] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  async function sendTest(): Promise<void> {
    setTestError(null);
    try {
      const result = await slackTest.mutateAsync();
      if (!result.ok) {
        setTestError('The test message could not be delivered. Please try again.');
        return;
      }
      setDelivered(true);
    } catch {
      setTestError('The test message could not be delivered. Please try again.');
    }
  }

  return (
    <>
      <DialogHeader
        slack={slack}
        title="Send a test message"
        subtitle={
          <>
            Confirm {providerName} can post to <strong>#{slack.channel}</strong>.
          </>
        }
        onClose={onCancel}
      />
      <Stack sx={{ px: 4, py: 3, gap: 2 }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${radius.lg}px`,
            bgcolor: 'background.page',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: `${radius.lg}px`,
              bgcolor: '#EEE8FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* The Slack app identity is not white-labelled. */}
            <Logo variant="glyph" height={23} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Alison</Typography>
              <Typography sx={{ fontSize: 8, fontWeight: 700, color: 'text.secondary' }}>
                APP
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>now</Typography>
            </Stack>
            <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
              {providerName} is connected. You&apos;ll get creative scores, KPI alerts and weekly
              digests right here in #{slack.channel}.
            </Typography>
          </Box>
        </Stack>
        {delivered && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 2,
              borderRadius: `${radius.lg}px`,
              bgcolor: 'success.bg',
              color: 'success.dark',
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckIcon sx={{ fontSize: 12 }} />
            </Box>
            <Typography sx={{ fontSize: 11 }}>
              Test message delivered to #{slack.channel}.
            </Typography>
          </Stack>
        )}
        {testError && <Alert severity="error">{testError}</Alert>}
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          px: 4,
          py: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.page',
        }}
      >
        <Button size="small" variant="text" onClick={onFinish} sx={{ color: 'text.secondary' }}>
          Skip
        </Button>
        <Box sx={{ flex: 1 }} />
        {delivered ? (
          <Button size="small" variant="gradient" startIcon={<CheckIcon />} onClick={onFinish}>
            Finish setup
          </Button>
        ) : (
          <Button
            size="small"
            variant="gradient"
            startIcon={
              slackTest.isPending ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                <SendOutlinedIcon />
              )
            }
            disabled={slackTest.isPending}
            onClick={() => void sendTest()}
          >
            Send test message
          </Button>
        )}
      </Stack>
    </>
  );
}

export function SlackSetupDialog({
  open,
  step,
  slack,
  onCancel,
  onReconnect,
  onConfigured,
  onFinish,
}: SlackSetupDialogProps): ReactNode {
  const width = step === 'configure' ? 560 : step === 'redirecting' ? 420 : 480;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="slack-setup-title"
      slotProps={{
        backdrop: {
          sx: { bgcolor: 'rgba(15, 23, 43, 0.42)', backdropFilter: 'blur(4px)' },
        },
        paper: {
          sx: {
            width,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 80px)',
            m: 3,
            borderRadius: `${radius.xl}px`,
            overflow: 'hidden',
            boxShadow: '0 24px 72px rgba(15, 23, 43, 0.30)',
          },
        },
      }}
    >
      {step === 'redirecting' && <Redirecting onCancel={onCancel} />}
      {step === 'configure' && (
        <Configure
          slack={slack}
          onCancel={onCancel}
          onReconnect={onReconnect}
          onConfigured={onConfigured}
        />
      )}
      {step === 'test' && <TestMessage slack={slack} onCancel={onCancel} onFinish={onFinish} />}
    </Dialog>
  );
}
